import { useFlashSale } from '@/containers/FlashSaleContainer'
import { useEffect, useMemo, useState } from 'react'

interface FlashSaleProductCountdownProps {
  endTime: number
  productId?: string
  className?: string
  light?: boolean
  size?: 'small' | 'large'
}

const second = 1000
const minute = second * 60
const hour = minute * 60
const day = hour * 24

const FlashSaleProductCountdown = ({
  endTime,
  productId,
  className,
  light,
  size = 'small',
}: FlashSaleProductCountdownProps) => {
  const { dispatch } = useFlashSale()
  const savedTime = new Date(endTime)
  const countDownDate = new Date(savedTime).getTime()
  const [countDown, setCountDown] = useState(
    countDownDate - new Date().getTime()
  )

  const seconds = useMemo(
    () => Math.max(0, Math.floor((countDown % minute) / second)),
    [countDown]
  )

  const minutes = useMemo(
    () => Math.max(0, Math.floor((countDown % hour) / minute)),
    [countDown]
  )

  const days = useMemo(
    () => Math.max(0, Math.floor(countDown / day)),
    [countDown]
  )

  const hours = useMemo(
    () => Math.max(0, Math.floor((countDown % day) / hour + days * 24)),
    [countDown]
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setCountDown(countDownDate - new Date().getTime())
      if (countDownDate - new Date().getTime() < 0) {
        setCountDown(0)
        if (productId) {
          dispatch({ type: 'DELETE_PRODUCT', payload: { productId } })
        }
        clearInterval(interval)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [countDownDate])

  const hoursString = useMemo(() => {
    const _hours = hours > 24 ? hours % 24 : hours
    return _hours < 10 ? `0${_hours}` : `${_hours}`
  }, [hours])

  const minutesString = useMemo(
    () => (minutes < 10 ? `0${minutes}` : `${minutes}`),
    [minutes]
  )

  const secondsString = seconds < 10 ? `0${seconds}` : `${seconds}`

  const renderValue = (valueString: string) => (
    <div
      className={`h-full ${
        size === 'small' ? 'w-6 rounded' : 'w-12 rounded-sm'
      }  ${light ? 'bg-white' : 'bg-main'} flex items-center justify-center`}
    >
      <span className='block'>{valueString.charAt(0)}</span>
      <span className='block'>{valueString.charAt(1)}</span>
    </div>
  )
  const delimiter = (
    <span className={`delimiter ${light ? 'text-white' : 'text-main'}`}>:</span>
  )

  return (
    <div
      className={`countDown flex items-center font-extrabold leading-[18px] ${
        light ? 'text-main' : 'text-white'
      } gap-[3px] ${
        size === 'small' ? 'h-6 text-[14px]' : 'h-12 text-[24px]'
      } justify-center ${className}`}
    >
      {renderValue(hoursString)}
      {delimiter}
      {renderValue(minutesString)}
      {delimiter}
      {renderValue(secondsString)}
    </div>
  )
}

export default FlashSaleProductCountdown
